import React, { useEffect, useState } from "react";
import {
  Badge,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@lemonadetraining/web-ui";
import { IconTag, IconX } from "@tabler/icons-react";
import { WORKOUT_CATEGORIES } from "@lemonadetraining/contracts";

const NUMBER_OF_ALLOWED_TAG = 2;
let isInit = false;

interface WorkoutTagsProps {
  value: string[];
  onTagUpdate?: (tags: string[]) => void;
  readOnly?: boolean;
}

const WorkoutTags = (props: WorkoutTagsProps) => {
  const [tags, setTags] = useState<string[]>(props.value || []);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [search, setSearch] = useState("");
  const addTagPossible = tags.length < NUMBER_OF_ALLOWED_TAG;

  useEffect(() => {
    if (props.onTagUpdate) {
      props.onTagUpdate(tags);
    }

    if (!isInit) {
      isInit = true;
      return;
    }
  }, [tags]);

  const handleDelete = (tagToDelete: string) => () => {
    setTags((prev) => prev.filter((tag) => tag !== tagToDelete));
  };

  const handleSelect = (option: string) => {
    setTags((prev) => [...prev, option]);
    setPopoverOpen(false);
    setSearch("");
  };

  // Compare trimmed: stored workout tags are verbatim strings, and docs
  // created before the category constants were whitespace-normalized can
  // carry a trailing-space variant ("Strength / Weightlifting ") that must
  // still count as the same category.
  const filteredOptions = WORKOUT_CATEGORIES.filter(
    (cat) =>
      !tags.some((tag) => tag.trim() === cat.trim()) &&
      cat.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ml-1 flex flex-row items-center justify-end gap-2">
      {tags.map((data, idx) => (
        <Badge key={idx} variant="default" className="gap-1 pr-1">
          {data}
          {!props.readOnly && (
            <button
              type="button"
              onClick={handleDelete(data)}
              className="ml-0.5 rounded-full hover:bg-primary-foreground/20"
              aria-label={`Remove ${data}`}
            >
              <IconX size={14} />
            </button>
          )}
        </Badge>
      ))}
      {addTagPossible && !props.readOnly && (
        <Popover
          open={popoverOpen}
          onOpenChange={(open) => {
            setPopoverOpen(open);
            if (!open) setSearch("");
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Add Tags"
                  className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                >
                  <IconTag size={18} />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">Add Tags</TooltipContent>
          </Tooltip>
          <PopoverContent className="w-[200px] gap-1 p-1" align="start">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tags..."
              className="mb-1 h-7"
              autoFocus
            />
            <div className="max-h-[200px] overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className="w-full cursor-pointer rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                  >
                    {option}
                  </button>
                ))
              ) : (
                <p className="px-2 py-1.5 text-sm text-muted-foreground">No options</p>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default WorkoutTags;
